import {
  Banner,
  Box,
  Button,
  Dropdown,
  DropdownOption,
  Spinner,
  TextArea,
  TextField
} from "@modernatx-internal/component-library";
import React from "react";

import { useTextBlockRendering } from "@/components/TextBlock/TextBlockRenderContext";
import { ContentComponentProps, InquiryFormContent } from "@/content-V2/types";
import { useExperience } from "@/context/ExperienceContext";
import { useInquiryForm } from "@/hooks/useInquiryForm";

type FieldKey =
  | "fullNameKanji"
  | "fullNameKatakana"
  | "institution"
  | "department"
  | "occupation"
  | "workplacePhoneNumber"
  | "email"
  | "confirmEmail"
  | "message";

export const InquiryForm: React.FC<ContentComponentProps<InquiryFormContent>> = ({
  buttonText,
  emailEvent,
  globalErrorText,
  globalSuccessText,
  labelText,
  occupationOptions,
  validationText
}) => {
  const { editing } = useTextBlockRendering();
  const { error, loading, submitForm, success } = useInquiryForm();
  const { expandedMode } = useExperience();

  const [validationErrorVisible, validationErrorVisibleSet] = React.useState(false);

  const [touched, touchedSet] = React.useState<Record<FieldKey, boolean>>({
    fullNameKanji: false,
    fullNameKatakana: false,
    institution: false,
    department: false,
    occupation: false,
    workplacePhoneNumber: false,
    email: false,
    confirmEmail: false,
    message: false
  });

  const markTouched = React.useCallback((key: FieldKey) => {
    touchedSet((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const [fullNameKanji, fullNameKanjiSet] = React.useState("");
  const [fullNameKatakana, fullNameKatakanaSet] = React.useState("");
  const [institution, institutionSet] = React.useState("");
  const [department, departmentSet] = React.useState("");
  const [occupation, occupationSet] = React.useState<DropdownOption["value"]>(null);
  const [workplacePhoneNumber, workplacePhoneNumberSet] = React.useState("");
  const [email, emailSet] = React.useState("");
  const [confirmEmail, confirmEmailSet] = React.useState("");
  const [message, messageSet] = React.useState("");
  const occupationWrapRef = React.useRef<HTMLDivElement | null>(null);

  function normalizeInput(s: string): string {
    return s.replace(/\u3000/g, " ").trim();
  }

  function isNonempty(s: string): boolean {
    return normalizeInput(s).length > 0;
  }

  const fullNameKanjiValid = isNonempty(fullNameKanji);
  const fullNameKatakanaValid = isNonempty(fullNameKatakana);
  const institutionValid = isNonempty(institution);
  const departmentValid = isNonempty(department);
  const occupationValid = typeof occupation === "string" && occupation.length > 0;

  const emailTrim = normalizeInput(email);
  const confirmEmailTrim = normalizeInput(confirmEmail);

  const emailValid = !!emailTrim.length && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(emailTrim);
  const confirmEmailValid =
    !!confirmEmailTrim.length && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(confirmEmailTrim);

  const emailsMatch = emailTrim.length > 0 && emailTrim === confirmEmailTrim;

  const phoneValid = isNonempty(workplacePhoneNumber);
  const messageValid = isNonempty(message);

  const showFieldErrors = expandedMode || validationErrorVisible;

  const inlineRequiredErrorText = validationText?.genericInlineError;

  const fieldError = React.useCallback(
    (key: FieldKey, isValid: boolean) => {
      if (isValid) {
        return undefined;
      }
      if (!showFieldErrors && !touched[key]) {
        return undefined;
      }
      return inlineRequiredErrorText;
    },
    [inlineRequiredErrorText, showFieldErrors, touched]
  );

  const confirmEmailError =
    confirmEmailValid && !emailsMatch
      ? !showFieldErrors && !touched.confirmEmail
        ? undefined
        : validationText?.emailMatchError
      : fieldError("confirmEmail", confirmEmailValid);

  const formValid =
    fullNameKanjiValid &&
    fullNameKatakanaValid &&
    institutionValid &&
    departmentValid &&
    occupationValid &&
    phoneValid &&
    emailValid &&
    confirmEmailValid &&
    emailsMatch &&
    messageValid;

  const handleOccupationSelect = React.useCallback(
    (option: DropdownOption) => {
      occupationSet(option.value);
      markTouched("occupation");
    },
    [markTouched]
  );

  const handleSubmit = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!editing && formValid) {
        submitForm({
          emailEvent,
          payload: {
            fullNameKanji,
            fullNameKatakana,
            institution,
            department,
            occupation,
            workplacePhoneNumber,
            email,
            message
          }
        });
      }
    },
    [
      editing,
      emailEvent,
      formValid,
      submitForm,
      fullNameKanji,
      fullNameKatakana,
      institution,
      department,
      occupation,
      workplacePhoneNumber,
      email,
      message
    ]
  );

  const handleSubmitButtonClick = React.useCallback(() => {
    if (!formValid) {
      validationErrorVisibleSet(true);
      touchedSet({
        fullNameKanji: true,
        fullNameKatakana: true,
        institution: true,
        department: true,
        occupation: true,
        workplacePhoneNumber: true,
        email: true,
        confirmEmail: true,
        message: true
      });
    }
  }, [formValid]);

  return (
    <Box
      as="form"
      data-track="inquiry-form"
      data-track-form-validated={formValid}
      sx={{ display: "flex", flexDirection: "column", gap: 6, position: "relative" }}
      onSubmit={handleSubmit}
    >
      {error && (
        <Banner
          variant="critical"
          title={globalErrorText?.title}
          text={globalErrorText?.description}
        />
      )}

      {(expandedMode || success) && (
        <Banner
          variant="success"
          title={globalSuccessText?.title}
          text={globalSuccessText?.description}
        />
      )}

      {!success && (
        <>
          <Box sx={{ color: "text-default" }}>
            <TextField
              label={labelText?.fullNameKanji}
              maxLength={254}
              name="FullNameKanji"
              required={true}
              value={fullNameKanji}
              error={fieldError("fullNameKanji", fullNameKanjiValid)}
              onBlur={() => markTouched("fullNameKanji")}
              onChange={(e) => fullNameKanjiSet(e.currentTarget.value)}
            />
          </Box>

          <Box sx={{ color: "text-default" }}>
            <TextField
              label={labelText?.fullNameKatakana}
              maxLength={254}
              name="FullNameKatakana"
              required={true}
              value={fullNameKatakana}
              error={fieldError("fullNameKatakana", fullNameKatakanaValid)}
              onBlur={() => markTouched("fullNameKatakana")}
              onChange={(e) => fullNameKatakanaSet(e.currentTarget.value)}
            />
          </Box>

          <Box
            sx={{
              color: "text-default",
              display: "flex",
              gap: 6,
              flexDirection: ["column", "row", "row"]
            }}
          >
            <Box sx={{ flex: 1 }}>
              <TextField
                label={labelText?.institution}
                name="Institution"
                maxLength={254}
                required={true}
                value={institution}
                error={fieldError("institution", institutionValid)}
                onBlur={() => markTouched("institution")}
                onChange={(e) => institutionSet(e.currentTarget.value)}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <TextField
                label={labelText?.department}
                name="Department"
                maxLength={254}
                required={true}
                value={department}
                error={fieldError("department", departmentValid)}
                onBlur={() => markTouched("department")}
                onChange={(e) => departmentSet(e.currentTarget.value)}
              />
            </Box>
          </Box>

          <Box sx={{ color: "text-default" }}>
            <Box
              ref={occupationWrapRef}
              onBlurCapture={() => {
                setTimeout(() => {
                  if (typeof document === "undefined") {
                    return;
                  }

                  const wrap = occupationWrapRef.current;
                  const stillInside = !!wrap && wrap.contains(document.activeElement);

                  if (!stillInside && !occupationValid) {
                    markTouched("occupation");
                  }
                }, 0);
              }}
            >
              {occupationOptions && (
                <Dropdown
                  id="inquiry-occupation-dropdown"
                  dropdownOptions={occupationOptions}
                  label={labelText?.occupation}
                  required={true}
                  error={fieldError("occupation", occupationValid)}
                  showMenuOverride={expandedMode}
                  wrapOptions={true}
                  onSelectOption={handleOccupationSelect}
                />
              )}
            </Box>
          </Box>

          <Box sx={{ color: "text-default" }}>
            <TextField
              label={labelText?.workplacePhoneNumber}
              maxLength={15}
              name="WorkplacePhoneNumber"
              required={true}
              value={workplacePhoneNumber}
              error={fieldError("workplacePhoneNumber", phoneValid)}
              onBlur={() => markTouched("workplacePhoneNumber")}
              onChange={(e) => workplacePhoneNumberSet(e.currentTarget.value)}
            />
          </Box>

          <Box
            sx={{
              color: "text-default",
              display: "flex",
              gap: 6,
              flexDirection: ["column", "row", "row"]
            }}
          >
            <Box sx={{ flex: 1 }}>
              <TextField
                label={labelText?.email}
                maxLength={254}
                name="Email"
                required={true}
                value={email}
                error={fieldError("email", emailValid)}
                onBlur={() => markTouched("email")}
                onChange={(e) => emailSet(e.currentTarget.value)}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <TextField
                label={labelText?.confirmEmail}
                maxLength={254}
                name="ConfirmEmail"
                required={true}
                value={confirmEmail}
                error={confirmEmailError}
                onBlur={() => markTouched("confirmEmail")}
                onChange={(e) => confirmEmailSet(e.currentTarget.value)}
              />
            </Box>
          </Box>

          <Box sx={{ color: "text-default" }}>
            <TextArea
              rows={3}
              label={labelText?.message}
              name="Message"
              required={true}
              value={message}
              error={fieldError("message", messageValid)}
              onBlur={() => markTouched("message")}
              onChange={(e) => messageSet(e.currentTarget.value)}
            />
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Button
              brand="secondary"
              size="lg"
              sx={{ minWidth: 50 }}
              type="submit"
              onClick={handleSubmitButtonClick}
            >
              {buttonText}
            </Button>
          </Box>
        </>
      )}

      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          height: "100%",
          justifyContent: "center",
          left: 0,
          opacity: loading ? 0.8 : 0,
          pointerEvents: loading ? "auto" : "none",
          position: "absolute",
          top: 0,
          transition: "transition-color-slow",
          width: "100%"
        }}
      >
        <Spinner />
      </Box>
    </Box>
  );
};
